import { useState, useEffect, useMemo, useRef } from "react";
import { 
  Plus, Search, ArrowUpDown, FileCheck, MoreHorizontal, 
  Trash2, Archive, ArchiveRestore, Edit, Eye, Send, Download, 
  Copy, FileText, ArrowRight, Check, ChevronDown
} from "lucide-react";
import { quotationService } from "../../services/quotation.service";
import databaseService from "../../services/database.service";
import { showSmtpWarning } from "../../utils/smtpValidation";
import { useToast } from "../../hooks/useToast";
import { useConfirm } from "../../hooks/useConfirm";
import Toast from "../../ui/Toast";
import ConfirmDialog from "../../ui/ConfirmDialog";
import PortalDropdown from "../../ui/PortalDropdown";
import DropdownMenu from "../../ui/DropdownMenu";
import { formatCurrency } from "../../utils/formatCurrency";

export default function QuotationListPage({ onNavigateToEditor, refreshTrigger = 0, onNavigate }) {
  // State
  const [quotations, setQuotations] = useState([]);
  const [allQuotations, setAllQuotations] = useState([]); // For counting purposes
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Toast notifications
  const { toast, hideToast, success, error: showError, info } = useToast();
  
  // Confirmation dialogs
  const { dialog, confirm } = useConfirm();
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [archiveFilter, setArchiveFilter] = useState(false); // false = active, true = archived
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("DESC");

  // Row actions
  const [hoveredRow, setHoveredRow] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Sort dropdown
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortButtonRef = useRef(null);
  const [sortButtonRect, setSortButtonRect] = useState(null);

  /* ═══════════════════════════════════════════════════════════
     LOAD QUOTATIONS
     ═══════════════════════════════════════════════════════════ */
  const loadQuotations = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load ALL quotations (both archived and non-archived) for counting
      const allDataForCounting = await quotationService.list({
        search: "",
        sortBy: "updated_at",
        sortOrder: "DESC",
        // Don't filter by archived status for counting
      });
      setAllQuotations(allDataForCounting);

      // Load filtered quotations for display
      const filters = {
        search: searchQuery,
        sortBy,
        sortOrder,
        archived: archiveFilter,
      };

      // Only add status filter if not "all"
      if (statusFilter !== "all") {
        filters.status = statusFilter;
      }

      const filteredData = await quotationService.list(filters);
      
      // Enrich quotations with client names if missing
      const enrichedData = await Promise.all(
        filteredData.map(async (quotation) => {
          // If quotation has client_id but no client_name, fetch it
          if (quotation.client_id && !quotation.client_name) {
            try {
              const { clientService } = await import('../../services/client.service');
              const client = await clientService.get(quotation.client_id);
              if (client) {
                return {
                  ...quotation,
                  client_name: client.name,
                };
              }
            } catch (err) {
              console.warn('[QuotationList] Failed to fetch client:', err);
            }
          }
          return quotation;
        })
      );
      
      setQuotations(enrichedData);
    } catch (err) {
      console.error("Failed to load quotations:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load on mount and when filters change OR when refreshTrigger changes
  useEffect(() => {
    loadQuotations();
  }, [statusFilter, archiveFilter, searchQuery, sortBy, sortOrder, refreshTrigger]);

  /* ═══════════════════════════════════════════════════════════
     CREATE NEW QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const handleCreateQuotation = async () => {
    // Navigate to editor without creating quotation yet
    // The editor will create the quotation when user enters meaningful data
    onNavigateToEditor(null); // null means new quotation mode
  };

  /* ═══════════════════════════════════════════════════════════
     DELETE DRAFT QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const handleDeleteDraft = async (quotation, e) => {
    e?.stopPropagation(); // Prevent row click

    if (quotation.status !== "draft") {
      return; // Should never happen due to UI guards
    }

    const confirmed = await confirm({
      title: "Delete Draft Quotation?",
      message: `Quotation ${quotation.quote_number} will be permanently deleted. This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (!confirmed) return;

    setDeletingId(quotation.id);

    try {
      await quotationService.delete(quotation.id);
      
      // Show success feedback
      success(`Quotation ${quotation.quote_number} deleted`);
      
      // Refresh list
      await loadQuotations();
      
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to delete quotation:", err);
      showError(err.message || "Failed to delete quotation");
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     ARCHIVE QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const handleArchive = async (quotation, e) => {
    e?.stopPropagation();

    setDeletingId(quotation.id);

    try {
      await quotationService.archive(quotation.id);
      success(`Quotation ${quotation.quote_number} archived`);
      await loadQuotations();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to archive quotation:", err);
      showError(`Failed to archive quotation: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     UNARCHIVE QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const handleUnarchive = async (quotation, e) => {
    e?.stopPropagation();

    setDeletingId(quotation.id);

    try {
      await quotationService.unarchive(quotation.id);
      success(`Quotation ${quotation.quote_number} restored`);
      await loadQuotations();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to unarchive quotation:", err);
      showError(`Failed to restore quotation: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     DELETE ARCHIVED QUOTATION (PERMANENT)
     ═══════════════════════════════════════════════════════════ */
  const handleDeleteArchived = async (quotation, e) => {
    e?.stopPropagation();

    const confirmed = await confirm({
      title: "Delete Quotation Permanently?",
      message: `Quotation ${quotation.quote_number} will be permanently deleted. This action cannot be undone.`,
      confirmText: "Delete Permanently",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (!confirmed) return;

    setDeletingId(quotation.id);

    try {
      await quotationService.deleteArchived(quotation.id);
      success(`Quotation ${quotation.quote_number} permanently deleted`);
      await loadQuotations();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to delete archived quotation:", err);
      showError(err.message || "Failed to delete quotation");
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     VIEW QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const handleView = (quotation, e) => {
    e?.stopPropagation();
    onNavigateToEditor(quotation.id);
    setOpenMenuId(null);
  };

  /* ═══════════════════════════════════════════════════════════
     PREVIEW PDF
     ═══════════════════════════════════════════════════════════ */
  const handlePreview = (quotation, e) => {
    e?.stopPropagation();
    // Navigate to editor and trigger preview modal
    onNavigateToEditor(quotation.id, { action: 'preview' });
    setOpenMenuId(null);
  };

  /* ═══════════════════════════════════════════════════════════
     DOWNLOAD PDF
     ═══════════════════════════════════════════════════════════ */
  const handleDownload = (quotation, e) => {
    e?.stopPropagation();
    // Navigate to editor and trigger download modal
    onNavigateToEditor(quotation.id, { action: 'download' });
    setOpenMenuId(null);
  };

  /* ═══════════════════════════════════════════════════════════
     CONVERT TO INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleConvertToInvoice = async (quotation, e) => {
    e?.stopPropagation();

    const confirmed = await confirm({
      title: "Convert to Invoice?",
      message: `This will create a new invoice from quotation ${quotation.quote_number}. The quotation will remain unchanged.`,
      confirmText: "Convert to Invoice",
      cancelText: "Cancel",
      variant: "primary",
    });

    if (!confirmed) {
      setOpenMenuId(null);
      return;
    }

    try {
      info("Converting to invoice...");
      const result = await quotationService.convert(quotation.id);
      success(`Invoice ${result.invoice_number} created from quotation`);
      await loadQuotations();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to convert quotation:", err);
      showError(err.message || "Failed to convert to invoice");
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     SEND QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const handleSend = async (quotation, e) => {
    e?.stopPropagation();

    // 🔥 SMTP VALIDATION FIRST
    const canProceed = await showSmtpWarning(showError, confirm, () => {
      // Navigate to settings
      if (onNavigate) {
        onNavigate("settings");
      }
    });

    if (!canProceed) {
      setOpenMenuId(null);
      return; // Don't proceed if SMTP is not configured
    }

    const confirmed = await confirm({
      title: "Send Quotation?",
      message: `Quotation ${quotation.quote_number} will be marked as sent.`,
      confirmText: "Send Quotation",
      cancelText: "Cancel",
      variant: "primary",
    });

    if (!confirmed) {
      setOpenMenuId(null);
      return;
    }

    try {
      await quotationService.send(quotation.id);
      success(`Quotation ${quotation.quote_number} sent`);
      await loadQuotations();
    } catch (err) {
      showError(err.message || "Failed to send quotation");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     COPY QUOTATION LINK
     ═══════════════════════════════════════════════════════════ */
  const handleCopyLink = async (quotation, e) => {
    e?.stopPropagation();

    try {
      // Generate shareable link (placeholder)
      const link = `zylobill://quotation/${quotation.id}`;
      await navigator.clipboard.writeText(link);
      success("Quotation link copied to clipboard");
    } catch (err) {
      showError("Failed to copy link");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     DUPLICATE QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const handleDuplicate = async (quotation, e) => {
    e?.stopPropagation();

    try {
      info("Duplicating quotation...");
      
      // Use database service to duplicate quotation
      const result = await databaseService.duplicateQuotation(quotation.id);

      success(`Quotation duplicated as ${result.quote_number}`);
      await loadQuotations();
    } catch (err) {
      showError(err.message || "Failed to duplicate quotation");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     STATUS COUNTS
     ═══════════════════════════════════════════════════════════ */
  const statusCounts = useMemo(() => {
    const counts = {
      all: allQuotations.filter(q => !q.archived_at).length, // Only count non-archived for "all"
      draft: 0,
      sent: 0,
      converted: 0,
      expired: 0,
    };

    allQuotations.forEach((quotation) => {
      // Skip archived quotations for status counts
      if (quotation.archived_at) return;

      // Check if expired
      if (quotation.status === "sent" && new Date(quotation.valid_until) < new Date()) {
        counts.expired++;
      } else {
        if (counts.hasOwnProperty(quotation.status)) {
          counts[quotation.status]++;
        }
      }
    });

    return counts;
  }, [allQuotations]);

  /* ═══════════════════════════════════════════════════════════
     RENDER HELPERS
     ═══════════════════════════════════════════════════════════ */
  const getStatusBadge = (quotation) => {
    let status = quotation.status;
    let styles = {
      draft: "bg-slate-50 text-slate-600 border-slate-200",
      sent: "bg-blue-50 text-blue-700 border-blue-200",
      converted: "bg-purple-50 text-purple-700 border-purple-200",
      expired: "bg-amber-50 text-amber-700 border-amber-200",
    };

    // Check if expired
    if (status === "sent" && new Date(quotation.valid_until) < new Date()) {
      status = "expired";
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
          styles[status] || styles.draft
        }`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${
          status === "converted" ? "bg-purple-500" :
          status === "sent" ? "bg-blue-500" :
          status === "expired" ? "bg-amber-500" :
          "bg-slate-400"
        }`}></span>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuId) setOpenMenuId(null);
    };
    
    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuId]);

  return (
    <div className="h-full flex flex-col bg-slate-50/30">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">Quotations</h1>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1 hidden sm:block">
              Manage and track your quotations
            </p>
          </div>

          {/* Desktop: Button */}
          <button
            onClick={handleCreateQuotation}
            className="hidden lg:flex px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm items-center gap-2 cursor-pointer"
          >
            <Plus size={16} />
            New Quotation
          </button>
        </div>
      </div>

      {/* Mobile FAB (Floating Action Button) */}
      <button
        onClick={handleCreateQuotation}
        className="lg:hidden fixed bottom-6 right-6 z-50 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center cursor-pointer"
        aria-label="New Quotation"
      >
        <Plus size={24} />
      </button>

      {/* Status Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="
          flex items-center gap-1
          overflow-x-auto scrollbar-hide
          px-4 sm:px-6 lg:px-8
          -mb-px
        ">
          {[
            { key: "all", label: "All", archived: false },
            { key: "draft", label: "Draft", archived: false },
            { key: "sent", label: "Sent", archived: false },
            { key: "converted", label: "Converted", archived: false },
            { key: "expired", label: "Expired", archived: false },
            { key: "archived", label: "Archived", archived: true },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                if (tab.key === "archived") {
                  setArchiveFilter(true);
                  setStatusFilter("all");
                } else {
                  setArchiveFilter(false);
                  setStatusFilter(tab.key);
                }
              }}
              className={`
                px-3 sm:px-4 py-3 
                text-xs sm:text-sm font-medium 
                border-b-2 transition-colors
                whitespace-nowrap
                flex-shrink-0
                cursor-pointer
                ${
                  (tab.archived && archiveFilter) || (!tab.archived && !archiveFilter && statusFilter === tab.key)
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                }
              `}
            >
              {tab.label}
              <span className="ml-1.5 sm:ml-2 text-xs text-slate-500">
                ({tab.key === "archived" 
                  ? allQuotations.filter(q => q.archived_at).length 
                  : statusCounts[tab.key] || 0})
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Search & Sort */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1 max-w-full sm:max-w-md">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search quotations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-slate-600 hidden sm:inline">Sort by:</span>
            
            {/* Sort Dropdown Button */}
            <button
              ref={sortButtonRef}
              onClick={() => {
                setSortButtonRect(sortButtonRef.current?.getBoundingClientRect());
                setSortDropdownOpen(!sortDropdownOpen);
              }}
              className="flex-1 sm:flex-none px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors bg-white flex items-center gap-2 min-w-0 sm:min-w-[160px] justify-between cursor-pointer"
            >
              <span className="text-slate-700 truncate">
                {sortBy === "updated_at" && "Last Updated"}
                {sortBy === "quote_date" && "Quote Date"}
                {sortBy === "total" && "Amount"}
                {sortBy === "quote_number" && "Quote Number"}
              </span>
              <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
            </button>

            {/* Sort Order Toggle */}
            <button
              onClick={() => setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC")}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0 cursor-pointer"
              title={sortOrder === "ASC" ? "Ascending" : "Descending"}
            >
              <ArrowUpDown size={16} />
            </button>
          </div>

          {/* Sort Dropdown Menu */}
          {sortDropdownOpen && (
            <DropdownMenu
              anchorRect={sortButtonRect}
              onClose={() => setSortDropdownOpen(false)}
            >
              <SortOption
                label="Last Updated"
                value="updated_at"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("updated_at");
                  setSortDropdownOpen(false);
                }}
              />
              <SortOption
                label="Quote Date"
                value="quote_date"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("quote_date");
                  setSortDropdownOpen(false);
                }}
              />
              <SortOption
                label="Amount"
                value="total"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("total");
                  setSortDropdownOpen(false);
                }}
              />
              <SortOption
                label="Quote Number"
                value="quote_number"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("quote_number");
                  setSortDropdownOpen(false);
                }}
              />
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Quotation List */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-24 sm:pb-32">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-600">Loading quotations...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-600">Error: {error}</div>
          </div>
        ) : quotations.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                <FileCheck size={24} className="text-slate-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-slate-900">
                  {searchQuery || statusFilter !== "all"
                    ? "No quotations found"
                    : "No quotations yet"}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {searchQuery || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Create your first quotation to get started"}
                </p>
              </div>
              {!searchQuery && statusFilter === "all" && (
                <button
                  onClick={handleCreateQuotation}
                  className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Create your first quotation
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop: Table View */}
            <div className="hidden lg:block bg-white rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Quotation
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Client
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Quote Date
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Total Amount
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Last Updated
                    </th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {quotations.map((quotation) => (
                    <QuotationRow
                      key={quotation.id}
                      quotation={quotation}
                      isHovered={hoveredRow === quotation.id}
                      isMenuOpen={openMenuId === quotation.id}
                      isDeleting={deletingId === quotation.id}
                      onHover={() => setHoveredRow(quotation.id)}
                      onLeave={() => setHoveredRow(null)}
                      onClick={() => onNavigateToEditor(quotation.id)}
                      onMenuToggle={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === quotation.id ? null : quotation.id);
                      }}
                      onMenuClose={() => setOpenMenuId(null)}
                      onView={(e) => handleView(quotation, e)}
                      onPreview={(e) => handlePreview(quotation, e)}
                      onDownload={(e) => handleDownload(quotation, e)}
                      onConvertToInvoice={(e) => handleConvertToInvoice(quotation, e)}
                      onSend={(e) => handleSend(quotation, e)}
                      onCopyLink={(e) => handleCopyLink(quotation, e)}
                      onDuplicate={(e) => handleDuplicate(quotation, e)}
                      onArchive={(e) => handleArchive(quotation, e)}
                      onUnarchive={(e) => handleUnarchive(quotation, e)}
                      onDelete={(e) => handleDeleteDraft(quotation, e)}
                      onDeleteArchived={(e) => handleDeleteArchived(quotation, e)}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                      getStatusBadge={getStatusBadge}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: Card View */}
            <div className="lg:hidden space-y-3">
              {quotations.map((quotation) => (
                <QuotationCard
                  key={quotation.id}
                  quotation={quotation}
                  isMenuOpen={openMenuId === quotation.id}
                  isDeleting={deletingId === quotation.id}
                  onClick={() => onNavigateToEditor(quotation.id)}
                  onMenuToggle={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(openMenuId === quotation.id ? null : quotation.id);
                  }}
                  onMenuClose={() => setOpenMenuId(null)}
                  onView={(e) => handleView(quotation, e)}
                  onPreview={(e) => handlePreview(quotation, e)}
                  onDownload={(e) => handleDownload(quotation, e)}
                  onConvertToInvoice={(e) => handleConvertToInvoice(quotation, e)}
                  onSend={(e) => handleSend(quotation, e)}
                  onCopyLink={(e) => handleCopyLink(quotation, e)}
                  onDuplicate={(e) => handleDuplicate(quotation, e)}
                  onArchive={(e) => handleArchive(quotation, e)}
                  onUnarchive={(e) => handleUnarchive(quotation, e)}
                  onDelete={(e) => handleDeleteDraft(quotation, e)}
                  onDeleteArchived={(e) => handleDeleteArchived(quotation, e)}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                  getStatusBadge={getStatusBadge}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={hideToast}
          duration={toast.duration}
        />
      )}

      {/* Confirmation Dialog */}
      {dialog && <ConfirmDialog {...dialog} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   QUOTATION ROW COMPONENT
   ═══════════════════════════════════════════════════════════ */
function QuotationRow({
  quotation,
  isHovered,
  isMenuOpen,
  isDeleting,
  onHover,
  onLeave,
  onClick,
  onMenuToggle,
  onMenuClose,
  onView,
  onPreview,
  onDownload,
  onConvertToInvoice,
  onSend,
  onCopyLink,
  onDuplicate,
  onArchive,
  onUnarchive,
  onDelete,
  onDeleteArchived,
  formatDate,
  formatCurrency,
  getStatusBadge,
}) {
  const isDraft = quotation.status === "draft";
  const isSent = quotation.status === "sent";
  const isConverted = quotation.status === "converted";
  const isExpired = quotation.status === "sent" && new Date(quotation.valid_until) < new Date();
  const isArchived = !!quotation.archived_at;

  // Ref for menu button (trigger)
  const menuButtonRef = useRef(null);

  return (
    <tr
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
      className={`border-b border-slate-200 cursor-pointer transition-colors ${
        isHovered ? "bg-slate-50/50" : ""
      } ${isDeleting ? "opacity-50 pointer-events-none" : ""}`}
    >
      {/* Quote Number */}
      <td className="py-3.5 px-4">
        <div className="font-medium text-sm text-slate-900">
          {quotation.quote_number}
        </div>
      </td>

      {/* Client */}
      <td className="py-3.5 px-4">
        <div className="text-sm text-slate-900">
          {quotation.client_name || (
            <span className="text-slate-400 italic">No client</span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="py-3.5 px-4">{getStatusBadge(quotation)}</td>

      {/* Quote Date */}
      <td className="py-3.5 px-4">
        <div className="text-sm text-slate-700">
          {formatDate(quotation.quote_date || quotation.issue_date)}
        </div>
      </td>

      {/* Total Amount */}
      <td className="py-3.5 px-4 text-right">
        <div className="font-semibold text-sm text-slate-900 tabular-nums">
          {formatCurrency(quotation.total)}
        </div>
      </td>

      {/* Last Updated */}
      <td className="py-3.5 px-4">
        <div className="text-sm text-slate-500">
          {formatDate(quotation.updated_at)}
        </div>
      </td>

      {/* Actions */}
      <td className="py-3.5 px-4 relative">
        <div className="flex items-center justify-end">
          <button
            ref={menuButtonRef}
            onClick={onMenuToggle}
            className={`p-1.5 rounded-md transition-all ${
              isMenuOpen
                ? "bg-slate-100 text-slate-700 opacity-100"
                : isHovered
                ? "text-slate-400 hover:text-slate-600 hover:bg-slate-100 opacity-100"
                : "text-slate-400 opacity-0 pointer-events-none"
            }`}
            aria-label="More actions"
            aria-expanded={isMenuOpen}
            aria-haspopup="true"
          >
            <MoreHorizontal size={16} />
          </button>
        </div>

        {/* State-Aware Dropdown Menu (Portal) */}
        <PortalDropdown
          isOpen={isMenuOpen}
          onClose={onMenuClose}
          triggerRef={menuButtonRef}
          align="right"
        >
          {isArchived ? (
            // ARCHIVED QUOTATION
            <>
              <button
                onClick={onUnarchive}
                disabled={isDeleting}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <ArchiveRestore size={14} />
                Restore quotation
              </button>
              
              {/* Divider */}
              <div className="my-1 border-t border-slate-100" />
              
              <button
                onClick={onDeleteArchived}
                disabled={isDeleting}
                className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={14} />
                {isDeleting ? "Deleting..." : "Delete permanently"}
              </button>
            </>
          ) : (
            <>
              {/* PRIMARY ACTIONS */}
              <button
                onClick={onView}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Eye size={14} />
                {isDraft ? "Edit quotation" : "View quotation"}
              </button>
              <button
                onClick={onPreview}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <FileText size={14} />
                Preview PDF
              </button>
              <button
                onClick={onDownload}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Download size={14} />
                Download PDF
              </button>

              {/* Divider */}
              <div className="my-1 border-t border-slate-100" />

              {/* CONVERSION ACTION (Highlighted) */}
              <button
                onClick={onConvertToInvoice}
                className="w-full text-left px-3 py-2 text-indigo-700 hover:bg-indigo-50 transition-colors flex items-center gap-2 font-medium"
              >
                <ArrowRight size={14} />
                Convert to invoice
              </button>

              {/* Divider */}
              <div className="my-1 border-t border-slate-100" />

              {/* SHARING & UTILITY ACTIONS */}
              {isDraft && (
                <button
                  onClick={onSend}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                >
                  <Send size={14} />
                  Send quotation
                </button>
              )}
              <button
                onClick={onCopyLink}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Copy size={14} />
                Copy quotation link
              </button>
              <button
                onClick={onDuplicate}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Copy size={14} />
                Duplicate quotation
              </button>

              {/* Divider */}
              <div className="my-1 border-t border-slate-100" />

              {/* DESTRUCTIVE / FINAL ACTIONS */}
              <button
                onClick={onArchive}
                disabled={isDeleting}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Archive size={14} />
                Archive quotation
              </button>
              
              {isDraft && (
                <button
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} />
                  {isDeleting ? "Deleting..." : "Delete quotation"}
                </button>
              )}
            </>
          )}
        </PortalDropdown>
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════
   QUOTATION CARD COMPONENT (MOBILE)
   ═══════════════════════════════════════════════════════════ */
function QuotationCard({
  quotation,
  isMenuOpen,
  isDeleting,
  onClick,
  onMenuToggle,
  onMenuClose,
  onView,
  onPreview,
  onDownload,
  onConvertToInvoice,
  onSend,
  onCopyLink,
  onDuplicate,
  onArchive,
  onUnarchive,
  onDelete,
  onDeleteArchived,
  formatDate,
  formatCurrency,
  getStatusBadge,
}) {
  const isDraft = quotation.status === "draft";
  const isArchived = !!quotation.archived_at;

  // Ref for menu button (trigger)
  const menuButtonRef = useRef(null);

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-xl border border-slate-200 p-4 
        cursor-pointer transition-all duration-150
        hover:shadow-md hover:border-slate-300
        active:scale-[0.98]
        ${isDeleting ? "opacity-50 pointer-events-none" : ""}
      `}
    >
      {/* Top Row: Client Name + Status Badge */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base text-slate-900 truncate">
            {quotation.client_name || (
              <span className="text-slate-400 italic">No client</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {getStatusBadge(quotation)}
          <button
            ref={menuButtonRef}
            onClick={onMenuToggle}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            aria-label="More actions"
          >
            <MoreHorizontal size={18} />
          </button>
        </div>
      </div>

      {/* Middle Row: Quotation Number + Date */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-sm text-slate-600">
          {quotation.quote_number}
        </div>
        <div className="text-sm text-slate-500">
          {formatDate(quotation.quote_date)}
        </div>
      </div>

      {/* Bottom Row: Amount */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Total Amount
        </div>
        <div className="text-lg font-semibold text-slate-900 tabular-nums">
          {formatCurrency(quotation.total)}
        </div>
      </div>

      {/* State-Aware Dropdown Menu (Portal) */}
      <PortalDropdown
        isOpen={isMenuOpen}
        onClose={onMenuClose}
        triggerRef={menuButtonRef}
        align="right"
      >
        {isArchived ? (
          // ARCHIVED QUOTATION
          <>
            <button
              onClick={onUnarchive}
              disabled={isDeleting}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArchiveRestore size={14} />
              Restore quotation
            </button>
            
            {/* Divider */}
            <div className="my-1 border-t border-slate-100" />
            
            <button
              onClick={onDeleteArchived}
              disabled={isDeleting}
              className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={14} />
              {isDeleting ? "Deleting..." : "Delete permanently"}
            </button>
          </>
        ) : (
          <>
            {/* PRIMARY ACTIONS */}
            <button
              onClick={onView}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <Eye size={14} />
              {isDraft ? "Edit quotation" : "View quotation"}
            </button>
            <button
              onClick={onPreview}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <FileText size={14} />
              Preview PDF
            </button>
            <button
              onClick={onDownload}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <Download size={14} />
              Download PDF
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-slate-100" />

            {/* CONVERSION ACTION (Highlighted) */}
            <button
              onClick={onConvertToInvoice}
              className="w-full text-left px-3 py-2 text-indigo-700 hover:bg-indigo-50 transition-colors flex items-center gap-2 font-medium"
            >
              <ArrowRight size={14} />
              Convert to invoice
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-slate-100" />

            {/* SHARING & UTILITY ACTIONS */}
            {isDraft && (
              <button
                onClick={onSend}
                className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Send size={14} />
                Send quotation
              </button>
            )}
            <button
              onClick={onCopyLink}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <Copy size={14} />
              Copy quotation link
            </button>
            <button
              onClick={onDuplicate}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <Copy size={14} />
              Duplicate quotation
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-slate-100" />

            {/* DESTRUCTIVE / FINAL ACTIONS */}
            <button
              onClick={onArchive}
              disabled={isDeleting}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Archive size={14} />
              Archive quotation
            </button>
            
            {isDraft && (
              <button
                onClick={onDelete}
                disabled={isDeleting}
                className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={14} />
                {isDeleting ? "Deleting..." : "Delete quotation"}
              </button>
            )}
          </>
        )}
      </PortalDropdown>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   SORT OPTION COMPONENT
   ═══════════════════════════════════════════════════════════ */
function SortOption({ label, value, currentValue, onClick }) {
  const isSelected = value === currentValue;
  
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between gap-3 cursor-pointer
        ${isSelected 
          ? "bg-slate-50 text-slate-900 font-medium" 
          : "text-slate-700 hover:bg-slate-50"
        }
      `}
    >
      <span>{label}</span>
      {isSelected && (
        <Check size={14} className="text-slate-900" />
      )}
    </button>
  );
}
